<template>
  <v-card elevation="0">
    <v-alert
      v-if="!businessHours.isWithin(tableStartAtTemp)"
      dense
      type="warning"
    >
      0時をまたぐため、日付を更新してください。
    </v-alert>
    <v-card-text class="d-flex align-end ma-0 pa-0">
      <v-text-field
        v-model="tableStartAtTemp"
        prefix="入店時間："
        type="datetime-local"
        hide-details
        class="pa-0 ma-0"
        @mousedown="isChanging = true"
        @blur="updateTableStartAt"
      >
        <template #prepend>
          <v-icon
            :color="isExpired ? 'error' : 'primary'"
            left
          >
            {{ icons.mdiPower }}
          </v-icon>
        </template>
      </v-text-field>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  ref,
  computed,
  getCurrentInstance,
  watch,
} from '@vue/composition-api'
import {
  mdiPower,
  mdiCheckCircleOutline,
  mdiCloseCircleOutline,
} from '@mdi/js'
import { add } from 'date-fns'
import TableApi from '@/api/waiter/Table'
import useCurrentData from '@/views/composable/useCurrentData'
import useDateFormat from '@/views/composable/useDateFormat'
import { BusinessHours } from '@/utils/businessHour'

export default {
  props: {
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    today: {
      type: String,
      required: true,
    },
    startAt: {
      type: String,
      required: true,
    },
    expireAt: {
      type: String,
      required: false,
      default: null,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const { currentClub } = useCurrentData()
    const { dateTimeForJsFriendly } = useDateFormat()
    const { openAt, closeAt } = currentClub.value
    const businessHours = new BusinessHours({ today: props.today, openAt, closeAt })

    const tableStartAtTemp = ref(dateTimeForJsFriendly(props.startAt))
    const isChanging = ref(false)

    const isExpired = computed(() => {
      return Date.parse(props.expireAt) - new Date() <= 0
    })

    const maxDate = computed(() => {
      return add(new Date(props.startAt), { days: 1 })
    })

    const cancel = () => {
      tableStartAtTemp.value = dateTimeForJsFriendly(props.startAt)
      isChanging.value = false
    }

    watch(() => isChanging.value, (newBool, _) => {
      if (!newBool) return

      if (isExpired.value) {
        vm.$toast.info(`現在アクティブな${currentClub.value.courseAlias}がありません。入店時間の更新は${currentClub.value.courseAlias}の時間に影響しません。`)
      } else {
        vm.$toast.warning(`入店時間の更新は現在アクティブな${currentClub.value.courseAlias}の時間も更新します。`)
      }
    })

    const updateTableStartAt = async () => {
      if (dateTimeForJsFriendly(tableStartAtTemp.value) === dateTimeForJsFriendly(props.startAt)) return

      if (!tableStartAtTemp.value) {
        cancel()

        return
      }

      // TODO: 営業日範囲チェック（一時的にコメントアウト中）
      // if (!businessHours.isWithin(tableStartAtTemp.value)) {
      //   vm.$toast.error('入店時間は当日の営業時間内で設定してください')
      //   cancel()
      //
      //   return
      // }

      try {
        const res = await TableApi.updateTable({
          tableId: props.tableId,
          params: { startAt: tableStartAtTemp.value },
        })

        if (res?.data?.data) {
          const tableData = res.data.data
          emit('updated:table', tableData)
          emit('updated:vending-histories', tableData.attributes.vendingHistories.data)

          vm.$toast.success('入店時間を更新しました')
        }
      } catch (error) {
        const status = error?.response?.status
        if (status === 404) {
          vm.$toast.error('対象の卓が見つかりません')
        } else if (status === 422) {
          vm.$toast.error('入店時間の更新に失敗しました')
        } else {
          vm.$toast.error('予期しないエラーが発生しました')
        }
        cancel()
      } finally {
        isChanging.value = false
      }
    }

    const cancelChanging = () => {
      tableStartAtTemp.value = props.startAt
    }

    return {
      // data
      tableStartAtTemp,
      isChanging,

      // computed
      isExpired,
      maxDate,

      // methods
      updateTableStartAt,
      cancelChanging,
      cancel,
      businessHours,

      icons: {
        mdiPower,
        mdiCheckCircleOutline,
        mdiCloseCircleOutline,
      },
    }
  },
}
</script>
